import React, { useState } from 'react';
import { CurrencyBtc, CreditCard, Copy, Check } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TokenCheckout = () => {
  const [selectedPack, setSelectedPack] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('crypto');
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [txReference, setTxReference] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState('');

  const packs = [
    { id: 'scout', name: 'Scout', price: 25, tokens: 5 },
    { id: 'federation', name: 'Federation', price: 50, tokens: 10 },
    { id: 'federation', name: 'Federation', price: 100, tokens: 20 }
  ];

  const cryptoWallets = [
    { symbol: 'BTC', name: 'Bitcoin', address: 'bc1qz6tyg6a4ks2epjkv0fsvc7pvfdps4lv9kl56ge' },
    { symbol: 'ETH', name: 'Ethereum', address: '0x8AaEd6eb7fF31FE91bB16a44c314b7678d1C48e6' },
    { symbol: 'WLD', name: 'Worldcoin', address: '0x8AaEd6eb7fF31FE91bB16a44c314b7678d1C48e6' },
    { symbol: 'SOL', name: 'Solana', address: '4qZ2SHqjpAqxL72yaKPGWx2XcaHwwBGcKmUgX847TiQc' },
    { symbol: 'ATOM', name: 'Cosmos', address: 'cosmos1uy8pjxsa6a4qmyadylxq8arcy6stcesfar2ndm', memo: '88968277' },
    { symbol: 'DOGE', name: 'Dogecoin', address: 'DNN5pMdGq7mcrYAbRaR1DZPS5aCWTeN58x' },
    { symbol: 'XRP', name: 'Ripple', address: 'rKHKDm92HagSa8tYja1FLhuHJyx2Wmz52y', destinationTag: '3192462127' }
  ];

  const paypalQRCodes = {
    scout: 'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/0f2dae150c07470893dbc3b062c84247_IMG_4374.JPG',
    federation: 'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/6d48667d6de64467928d5faf4b194386_IMG_4371.JPG',
    federation: 'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/ed92c27dc118409b9a2138b133903d52_IMG_4373.JPG'
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedAddress(label);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopiedAddress(''), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPack || !txReference.trim()) {
      toast.error('Please select a pack and enter your transaction reference');
      return;
    }

    setIsSubmitting(true);

    try {
      const federationId = localStorage.getItem('federationId');
      const pack = packs.find(p => p.id === selectedPack);

      await axios.post(`${API}/tokens/purchase`, {
        federation_id: federationId,
        tokens_purchased: pack.tokens,
        payment_method: paymentMethod === 'crypto' ? `Crypto (${selectedCrypto})` : 'PayPal',
        tx_reference: txReference
      });

      toast.success('Payment submitted successfully! Your tokens will be credited after verification.');
      setTxReference('');
      setSelectedPack(null);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Purchase submission failed:', error);
      }
      toast.error('Failed to submit purchase. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-16" data-testid="token-checkout-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <CurrencyBtc size={64} weight="duotone" className="neon-cyan mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tighter">Token Checkout</h1>
          <p className="text-xl text-gray-400">$5 = 1 Token • Crypto & PayPal Accepted</p>
          <a href="/crypto-guide" className="text-sm text-[#00F0FF] hover:underline mt-2 inline-block" data-testid="crypto-guide-link">
            New to crypto? Read our guide →
          </a>
        </div>

        {/* Quick $5 Purchase Button */}
        <div className="mb-12 text-center">
          <button
            onClick={() => setSelectedPack('scout')}
            className="payment-widget px-12 py-5 bg-[#00F0FF] text-[#030305] font-bold text-xl rounded-sm hover:bg-[#00D9E8] transition-colors shadow-2xl hover-lift"
            data-merchant-id="mer_nextxus_federation"
            data-testid="btn-quick-purchase-5"
          >
            💰 Purchase Token — $5
          </button>
          <p className="text-sm text-gray-500 mt-3">Or choose a token pack below for bulk savings</p>
        </div>

        {/* Token Packs */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {packs.map((pack, idx) => (
            <motion.div
              key={pack.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              onClick={() => setSelectedPack(pack.id)}
              className={`p-8 cursor-pointer rounded-sm border-2 transition-all ${
                selectedPack === pack.id
                  ? 'border-[#00F0FF] bg-[#00F0FF]/5 shadow-lg shadow-[#00F0FF]/20'
                  : 'border-white/10 bg-[#0B0E14] hover:border-white/30'
              }`}
              data-testid={`pack-${pack.id}`}
            >
              <h3 className="text-2xl font-bold mb-2 neon-cyan">{pack.name}</h3>
              <div className="text-4xl font-black mb-4">${pack.price}</div>
              <div className="mono text-lg text-gray-400">{pack.tokens} Tokens</div>
            </motion.div>
          ))}
        </div>

        {/* Payment Method Selector */}
        {selectedPack && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6">Select Payment Method</h2>
            <div className="flex gap-4">
              <button
                onClick={() => setPaymentMethod('crypto')}
                className={`flex-1 py-4 px-6 rounded-sm font-bold transition-all ${
                  paymentMethod === 'crypto'
                    ? 'bg-[#00F0FF] text-[#030305]'
                    : 'border border-white/20 text-white hover:border-[#00F0FF]'
                }`}
                data-testid="payment-method-crypto"
              >
                Cryptocurrency
              </button>
              <button
                onClick={() => setPaymentMethod('paypal')}
                className={`flex-1 py-4 px-6 rounded-sm font-bold transition-all ${
                  paymentMethod === 'paypal'
                    ? 'bg-[#00F0FF] text-[#030305]'
                    : 'border border-white/20 text-white hover:border-[#00F0FF]'
                }`}
                data-testid="payment-method-paypal"
              >
                PayPal Direct Deposit
              </button>
            </div>
          </motion.div>
        )}

        {/* Crypto Wallets */}
        {selectedPack && paymentMethod === 'crypto' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-12"
            data-testid="crypto-wallets-section"
          >
            <h2 className="text-2xl font-bold mb-6">Send Payment To:</h2>
            <div className="space-y-4">
              {cryptoWallets.map((wallet) => (
                <div
                  key={wallet.symbol}
                  onClick={() => setSelectedCrypto(wallet.symbol)}
                  className={`p-6 rounded-sm border cursor-pointer transition-all ${
                    selectedCrypto === wallet.symbol
                      ? 'border-[#00F0FF] bg-[#00F0FF]/5'
                      : 'border-white/10 bg-[#0B0E14] hover:border-white/30'
                  }`}
                  data-testid={`crypto-wallet-${wallet.symbol.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-bold text-lg">{wallet.symbol}</span>
                      <span className="text-gray-400 ml-2">({wallet.name})</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(wallet.address, wallet.symbol);
                      }}
                      className="p-2 rounded hover:bg-white/10 transition-colors"
                      data-testid={`copy-${wallet.symbol.toLowerCase()}`}
                    >
                      {copiedAddress === wallet.symbol ? (
                        <Check size={20} className="text-green-400" />
                      ) : (
                        <Copy size={20} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                  <div className="mono text-sm text-gray-300 break-all bg-black/30 p-3 rounded">
                    {wallet.address}
                  </div>
                  {wallet.memo && (
                    <div className="mt-3 p-3 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded">
                      <span className="text-[#FFB800] font-bold text-sm">⚠️ MEMO REQUIRED: </span>
                      <span className="mono text-sm text-white">{wallet.memo}</span>
                    </div>
                  )}
                  {wallet.destinationTag && (
                    <div className="mt-3 p-3 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded">
                      <span className="text-[#FFB800] font-bold text-sm">⚠️ DESTINATION TAG REQUIRED: </span>
                      <span className="mono text-sm text-white">{wallet.destinationTag}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* PayPal QR Code */}
        {selectedPack && paymentMethod === 'paypal' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-12"
            data-testid="paypal-qr-section"
          >
            <h2 className="text-2xl font-bold mb-6">Scan QR Code to Pay via PayPal:</h2>
            <div className="max-w-md mx-auto p-8 glass-surface border border-white/10 rounded-sm">
              <img
                src={paypalQRCodes[selectedPack]}
                alt={`PayPal QR for ${selectedPack}`}
                className="w-full rounded"
                data-testid="paypal-qr-image"
              />
              <p className="text-center text-gray-400 text-sm mt-4 mono">
                Scan with PayPal app to send ${packs.find(p => p.id === selectedPack)?.price}
              </p>
            </div>
          </motion.div>
        )}

        {/* Submission Form */}
        {selectedPack && (
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onSubmit={handleSubmit}
            className="max-w-2xl mx-auto p-8 glass-surface border border-white/10 rounded-sm"
            data-testid="payment-submission-form"
          >
            <h2 className="text-2xl font-bold mb-6">Submit Payment Proof</h2>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                {paymentMethod === 'crypto' ? 'Transaction ID / Hash' : 'PayPal Email'}
              </label>
              <input
                type="text"
                value={txReference}
                onChange={(e) => setTxReference(e.target.value)}
                placeholder={paymentMethod === 'crypto' ? 'Enter your transaction hash...' : 'Enter your PayPal email...'}
                className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-sm text-white mono text-sm focus:outline-none focus:border-[#00F0FF] transition-colors"
                data-testid="tx-reference-input"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-[#00F0FF] text-[#030305] font-bold rounded-sm hover:bg-[#00D9E8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-purchase-btn"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Purchase'}
            </button>
            <p className="text-xs text-gray-500 text-center mt-4">
              Your purchase will be manually verified by our team. Tokens will be credited within 24 hours.
            </p>
          </motion.form>
        )}
      </div>
    </div>
  );
};

export default TokenCheckout;